import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import {
    CreateTransaction,
    PudoTransactionReportListResponse,
    SearchTransactionQueryParams,
    Transaction,
    TransactionListResponse,
    TransactionQueryParams,
    UpdateTransaction
} from '@frontend/transaction/types';

export class TransactionClient extends APIClient {
    public static readonly endpoint = '/transaction-api/v1';
    public static readonly extendedEndpoint = '/transaction-api/ext/v1';

    public static async fetchTransactions(queryParams?: ApiQueryParams<TransactionQueryParams>): Promise<TransactionListResponse> {
        return await this.apiPaginated<TransactionListResponse, TransactionQueryParams>(`${this.endpoint}/transactions`, queryParams);
    }

    public static async fetchTransactionsNotInGroup(
        accountId: string,
        groupId: string,
        queryParams?: ApiQueryParams<DefaultQueryParams>
    ): Promise<TransactionListResponse> {
        return await this.apiPaginated<TransactionListResponse, DefaultQueryParams>(
            `${this.endpoint}/accounts/${accountId}/groups/${groupId}/transactions`,
            queryParams
        );
    }

    public static async searchTransactions(queryParams?: ApiQueryParams<DefaultQueryParams | SearchTransactionQueryParams>): Promise<TransactionListResponse> {
        return await this.apiPaginated<TransactionListResponse, DefaultQueryParams | SearchTransactionQueryParams>(
            `${this.endpoint}/transactions-search`,
            queryParams
        );
    }

    public static async resolveTransactions(ids: string[], field: string = 'id'): Promise<TransactionListResponse> {
        return this.resolve<TransactionListResponse>(`${this.endpoint}/transactions`, ids, field);
    }

    /**
     *
     * @deprecated
     */
    public static async fetchAccountTransactions(accountId: string, queryParams?: ApiQueryParams<DefaultQueryParams>): Promise<TransactionListResponse> {
        return await this.apiPaginated<TransactionListResponse, DefaultQueryParams>(`${this.endpoint}/accounts/${accountId}/transactions`, queryParams);
    }

    public static async postTransaction(accountId: string, createTransaction: CreateTransaction): Promise<Transaction> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/transactions`, createTransaction);
        return await this.handleResponse<Transaction>(response);
    }

    public static async fetchTransactionUniqueIdentifier(accountId: string): Promise<unknown> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/transactions-unique-identifier`);
        return await this.handleResponse<unknown>(response);
    }

    public static async fetchTransaction(accountId: string, transactionId: string): Promise<Transaction> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/transactions/${transactionId}`);
        return await this.handleResponse<Transaction>(response);
    }

    public static async patchTransaction(accountId: string, transactionId: string, updateTransaction: UpdateTransaction): Promise<Transaction> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/transactions/${transactionId}`, updateTransaction);
        return await this.handleResponse<Transaction>(response);
    }

    public static async deleteTransaction(account_id: string, transactionId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${account_id}/transactions/${transactionId}`);
        return await this.handleVoidResponse(response);
    }

    public static async deleteTransactions(transactionIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/transactions-delete`, { ids: transactionIds });
        return await this.handleVoidResponse(response);
    }

    // Report
    /**
     * @deprecated
     * @param queryParams
     * @returns
     */
    public static async fetchReportPudoTransactions(queryParams?: ApiQueryParams<TransactionQueryParams>): Promise<PudoTransactionReportListResponse> {
        return await this.apiPaginated<PudoTransactionReportListResponse, TransactionQueryParams>(`${this.endpoint}/report-pudo-transactions`, queryParams);
    }

    public static async postIoTSeed(accountId: string, iotId: string, body: { account_id: string }): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/iots/${iotId}/seed`, body);
        return this.handleResponse(response);
    }
}
